import axios, { InternalAxiosRequestConfig } from 'axios';
import { MediaAttachment, ChannelMessage, Channel, Community, Conversation, UserData, Notification, Post } from './types';
import { Capacitor } from '@capacitor/core';

const getApiUrl = () => {
    let url = '';
    const envUrl = process.env.REACT_APP_SERVER_URL;
    
    // 1. Determine base URL
    if (envUrl && envUrl.trim().length > 0) {
        url = envUrl.trim();
    } else if (Capacitor.isNativePlatform()) {
        if (process.env.NODE_ENV === 'development') {
            // Development: Use a stable Localtunnel URL (works across different Wi-Fi/Hotspots)
            url = `https://goodlyfe-dev.loca.lt`;
        } else {
            // Production: Always use the live server
            url = 'https://goodlyfe.net';
        }
    } else if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;

        if (hostname === 'goodlyfe.net' || hostname === 'www.goodlyfe.net') {
            url = 'https://goodlyfe.net';
        } else if (
            hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname.startsWith('192.168.') ||
            hostname.startsWith('10.') ||
            hostname.startsWith('172.')
        ) {
            url = `${protocol}//${hostname}:5000`;
        }
    }

    // Default to production if still empty
    if (!url) url = 'https://goodlyfe.net';

    // 2. Clean up trailing slashes strictly
    url = url.replace(/\/+$/, '');

    // 3. Ensure it ends with /api (but don't add if already there)
    if (!url.toLowerCase().endsWith('/api')) {
        url += '/api';
    }

    // 4. Return with single trailing slash to ensure relative paths join correctly.
    if (!url.endsWith('/')) {
        url += '/';
    }
    
    console.log('[API] Final URL:', url);
    return url;
};

export const API_URL = getApiUrl();
// console.log('[API] Using API URL:', API_URL); // Commented out to reduce noise, but kept as it was in original

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
    }
});

// Log all requests
api.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    console.log('[API] Request:', request.method?.toUpperCase(), request.url, request.data);
    return request;
});

// --- USERS ---
export const fetchAllUsers = async () => {
    const response = await api.get('users/sync');
    return response.data;
};

export const signupUser = async (userData: any) => {
    const response = await api.post('auth/signup', userData);
    return response.data;
};

export const loginUser = async (credentials: any) => {
    const response = await api.post('auth/login', credentials);
    return response.data;
};

export const guestLoginUser = async () => {
    const response = await api.post('auth/guest-login');
    return response.data;
};

export const claimGuestAccount = async (data: any) => {
    const response = await api.post('auth/claim-guest-account', data);
    return response.data;
};

export const requestQuickLink = async (identifier: string) => {
    const response = await api.post('auth/quick-link/request', { identifier });
    return response.data;
};

export const verifyQuickLink = async (params: { token?: string; code?: string }) => {
    const response = await api.post('auth/quick-link/verify', params);
    return response.data;
};

export const updateUserOnServer = async (username: string, data: any) => {
    const response = await api.put(`users/${encodeURIComponent(username)}`, data);
    return response.data;
};

export const renameUserOnServer = async (username: string, newUsername: string) => {
    const response = await api.post(`users/${encodeURIComponent(username)}/rename`, { newUsername });
    return response.data;
};

export const deleteUserOnServer = async (username: string, adminUsername: string) => {
    const response = await api.delete(`users/${encodeURIComponent(username)}?adminUsername=${encodeURIComponent(adminUsername)}`);
    return response.data;
};

export const sendSystemBroadcast = async (
    adminUsername: string, 
    message: string, 
    media?: MediaAttachment[], 
    autoSend: boolean = false,
    targetPage?: {
        type: 'profile' | 'post' | 'communities' | 'community_detail' | 'mod' | 'pimp_my_ride' | 'hashtag' | string;
        userId?: string;
        postId?: number | string;
        communityId?: string;
        channelId?: string;
        modId?: string;
        tag?: string;
        query?: string;
    },
    postSettings?: {
        visibility?: 'public' | 'followers' | 'groups' | 'private';
        showCaption?: boolean;
        showOnProfile?: boolean;
        textSize?: 'xs' | 'sm' | 'med' | 'lg';
        textAlign?: 'left' | 'center' | 'right';
        themeColor?: string;
        textColor?: string;
        themeOpacity?: number;
        fontId?: string;
        postcardStyle?: string;
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall';
    }
) => {
    const response = await api.post('admin/broadcast-notification', { adminUsername, message, media, autoSend, targetPage, postSettings });
    return response.data;
};

export const deleteSystemBroadcast = async (id: number, adminUsername: string) => {
    const response = await api.delete(`admin/broadcast-notification/${id}`, {
        params: { adminUsername }
    });
    return response.data;
};

export const editSystemBroadcast = async (id: number, adminUsername: string, newText: string) => {
    const response = await api.put(`admin/broadcast-notification/${id}`, { adminUsername, newText });
    return response.data;
};

export const getAutoBroadcastIds = async () => {
    const response = await api.get('admin/auto-broadcasts');
    return response.data;
};

export const toggleAutoBroadcast = async (id: number, adminUsername: string) => {
    const response = await api.post(`admin/broadcast-notification/${id}/toggle-auto-send`, { adminUsername });
    return response.data;
};

export const registerPushToken = async (username: string, token: string) => {
    console.log(`[API] Registering push token for ${username}: ${token.substring(0, 10)}...`);
    const response = await api.post(`users/${encodeURIComponent(username)}/fcm-token`, { token });
    return response.data;
};

export const sendTestPush = async (username: string) => {
    const res = await api.post('admin/test-push', { username });
    return res.data;
};

export const fetchSystemResources = async () => {
    const res = await api.get('admin/system-resources');
    return res.data;
};

export const fetchServerLogs = async () => {
    const res = await api.get('admin/server-logs');
    return res.data;
};

export const deactivateAccount = async (username: string, password: string) => {
    const response = await api.post(`users/${encodeURIComponent(username)}/deactivate`, { password });
    return response.data;
};

export const deleteAccountPermanently = async (username: string, password: string) => {
    const response = await api.post(`users/${encodeURIComponent(username)}/delete-account`, { password });
    return response.data;
};

// --- COMMUNITIES ---
export const createCommunityOnServer = async (data: any) => {
    const response = await api.post('communities', data);
    return response.data;
};

export const fetchAllCommunities = async (userId?: string) => {
    const url = userId ? `communities?userId=${encodeURIComponent(userId)}` : 'communities';
    const response = await api.get(url);
    return response.data;
};

export const fetchCommunityDetails = async (query: string) => {
    const response = await api.get(`communities/${encodeURIComponent(query)}`);
    return response.data;
};

export const joinCommunityOnServer = async (id: string, username: string, inviteLink?: string, requestingUser?: string) => {
    const response = await api.post(`communities/${id}/join`, { username, inviteLink, requestingUser });
    return response.data;
};

export const leaveCommunityOnServer = async (id: string, username: string) => {
    const response = await api.post(`communities/${id}/leave`, { username });
    return response.data;
};

export const fetchCommunityPosts = async (id: string, username?: string, inviteLink?: string, channelId?: string) => {
    let url = `communities/${id}/posts`;
    const params = new URLSearchParams();
    if (username) params.append('username', username);
    if (inviteLink) params.append('inviteLink', inviteLink);
    if (channelId) params.append('channelId', channelId);
    if (params.toString()) {
        url += `?${params.toString()}`;
    }
    const response = await api.get(url);
    return response.data;
};

export const approveCommunityPostOnServer = async (communityId: string, postId: number, username: string) => {
    const response = await api.put(`communities/${communityId}/posts/${postId}/approve`, { username });
    return response.data;
};

export const fetchJoinedCommunitiesPosts = async (username: string) => {
    const response = await api.get(`communities/feed/${username}`);
    return response.data;
};

export const updateCommunityOnServer = async (id: string, data: any) => {
    const response = await api.put(`communities/${id}`, data);
    return response.data;
};

export const grantAdminOnServer = async (id: string, requestingUser: string, targetUser: string) => {
    const response = await api.post(`communities/${id}/grant-admin`, { requestingUser, targetUser });
    return response.data;
};

export const revokeAdminOnServer = async (id: string, requestingUser: string, targetUser: string) => {
    const response = await api.post(`communities/${id}/revoke-admin`, { requestingUser, targetUser });
    return response.data;
};

export const removeMemberOnServer = async (id: string, requestingUser: string, targetUser: string) => {
    const response = await api.post(`communities/${id}/remove-member`, { requestingUser, targetUser });
    return response.data;
};

export const fetchChannelMessages = async (communityId: string, channelId: string, username?: string, inviteLink?: string) => {
    let url = `communities/${communityId}/channels/${channelId}/messages`;
    const params = new URLSearchParams();
    if (username) params.append('username', username);
    if (inviteLink) params.append('inviteLink', inviteLink);
    if (params.toString()) {
        url += `?${params.toString()}`;
    }
    const response = await api.get(url);
    return response.data;
};

export const sendChannelMessage = async (communityId: string, channelId: string, authorId: string, text: string, media?: MediaAttachment[], replyTo?: { id: string, text: string, authorId: string }, sharedPostId?: number, sharedPostBgUrl?: string) => {
    const response = await api.post(`communities/${communityId}/channels/${channelId}/messages`, { authorId, text, media, replyTo, sharedPostId, sharedPostBgUrl });
    return response.data as ChannelMessage;
};

export const reorderChannels = async (communityId: string, channels: Channel[]) => {
    const response = await api.put(`communities/${communityId}/channels/reorder`, { channels });
    return response.data as Community;
};

export const reactToChannelMessage = async (communityId: string, channelId: string, messageId: string, username: string, emoji: string) => {
    const response = await api.post(`communities/${communityId}/channels/${channelId}/messages/${messageId}/react`, { username, emoji });
    return response.data as ChannelMessage;
};

export const fetchChannelPresence = async (communityId: string, channelId: string) => {
    const response = await api.get(`communities/${communityId}/channels/${channelId}/presence`);
    return response.data;
};

export const deleteChannelMessage = async (communityId: string, channelId: string, messageId: string, username: string) => {
    const response = await api.delete(`communities/${communityId}/channels/${channelId}/messages/${messageId}?username=${encodeURIComponent(username)}`);
    return response.data;
};

export const editChannelMessage = async (communityId: string, channelId: string, messageId: string, username: string, text: string) => {
    const response = await api.put(`communities/${communityId}/channels/${channelId}/messages/${messageId}`, { username, text });
    return response.data as ChannelMessage;
};

// --- POSTS ---
export const fetchAllPosts = async (userId?: string) => {
    const ts = Date.now();
    const url = userId ? `posts?userId=${encodeURIComponent(userId)}&_t=${ts}` : `posts?_t=${ts}`;
    const response = await api.get(url);
    return response.data;
};

export const createPostOnServer = async (postData: any) => {
    const response = await api.post('posts', postData);
    return response.data;
};

export const updatePostOnServer = async (postId: number, data: any) => {
    const response = await api.put(`posts/${postId}`, data);
    return response.data;
};

export const incrementPlayCountOnServer = async (postId: number) => {
    const response = await api.post(`posts/${postId}/play`);
    return response.data;
};

export const deletePostOnServer = async (postId: number) => {
    const response = await api.delete(`posts/${postId}`);
    return response.data;
};

export const unbumpPostOnServer = async (originalPostId: number, username: string) => {
    const response = await api.post(`posts/${originalPostId}/unbump`, { username });
    return response.data;
};

export const toggleArchivePostOnServer = async (postId: number, isArchived: boolean) => {
    const response = await api.put(`posts/${postId}/archive`, { isArchived });
    return response.data;
};

// --- ACHIEVEMENTS ---
export const fetchAchievements = async () => {
    const response = await api.get('achievements');
    return response.data;
};

export const createAchievementOnServer = async (data: any) => {
    const response = await api.post('achievements', data);
    return response.data;
};

export const updateAchievementOnServer = async (id: number, data: any) => {
    const response = await api.put(`achievements/${id}`, data);
    return response.data;
};

export const deleteAchievementOnServer = async (id: number) => {
    const response = await api.delete(`achievements/${id}`);
    return response.data;
};

export const reorderAchievementsOnServer = async (orders: { id: number, order: number }[]) => {
    const response = await api.post('achievements/reorder', { orders });
    return response.data;
};

// --- NOTIFICATIONS ---
export const sendLinkRequest = async (username: string, targetUsers: string[], lat?: number, lng?: number, text?: string, imageUrl?: string, isLocation?: boolean, eventDate?: string, eventTime?: string) => {
    const response = await api.post(`users/${username}/link-request`, { targetUsers, lat, lng, text, imageUrl, isLocation, eventDate, eventTime });
    return response.data;
};

// --- CONVERSATIONS ---
export const fetchConversations = async (userId: string) => {
    const response = await api.get(`conversations?userId=${userId}`);
    return response.data;
};

export const createYipYapOnServer = async (data: { owner: string, groupIds?: string[], name: string, mode: string, visibility: string, visibilityGroups?: string[] }) => {
    const response = await api.post('conversations/yipyap', data);
    return response.data;
};

export const updateConversationOnServer = async (id: number, data: any) => {
    const response = await api.put(`conversations/${id}`, data);
    return response.data;
};

export const addMessageToConversationOnServer = async (id: number, message: any) => {
    const response = await api.post(`conversations/${id}/messages`, { message });
    return response.data;
};

export const deleteConversationOnServer = async (id: number, userId: string) => {
    const response = await api.delete(`conversations/${id}?userId=${userId}`);
    return response.data;
};

export const togglePinConversationOnServer = async (id: number, userId: string, pin: boolean) => {
    const response = await api.post(`conversations/${id}/pin`, { userId, pin });
    return response.data;
};

export const toggleMuteConversationOnServer = async (id: number, userId: string, mute: boolean) => {
    const response = await api.post(`conversations/${id}/mute`, { userId, mute });
    return response.data;
};

export const toggleUnreadConversationOnServer = async (id: number, userId: string, unread: boolean) => {
    const response = await api.post(`conversations/${id}/unread`, { userId, unread });
    return response.data;
};

export const deleteMessageFromConversationOnServer = async (id: number, messageId: number, username: string) => {
    const response = await api.delete(`conversations/${id}/messages/${messageId}?username=${encodeURIComponent(username)}`);
    return response.data;
};

export const editDirectMessageOnServer = async (id: number, messageId: number, userId: string, text: string) => {
    const response = await api.put(`conversations/${id}/messages/${messageId}`, { userId, text });
    return response.data as Conversation;
};

export const markConversationAsReadOnServer = async (id: number, userId: string) => {
    const response = await api.post(`conversations/${id}/read`, { userId });
    return response.data;
};

export const reactToMessageOnServer = async (conversationId: number, messageId: number, emoji: string, userId: string) => {
    const response = await api.post(`conversations/${conversationId}/messages/${messageId}/react`, { emoji, userId });
    return response.data;
};

export const startConversationOnServer = async (participants: string[]) => {
    const response = await api.post('conversations/start', { participants });
    return response.data;
};


export const requestPasswordReset = async (email: string) => {
    const response = await api.post('auth/forgot-password', { email });
    return response.data;
};

export const verifyResetToken = async (token: string) => {
    const response = await api.get(`auth/verify-reset-token/${token}`);
    return response.data;
};

export const resetPassword = async (token: string, password: string) => {
    const response = await api.post(`auth/reset-password/${token}`, { password });
    return response.data;
};

export const inviteToYipYap = async (conversationId: number, requestingUser: string, targetUserId: string) => {
    const response = await api.post(`conversations/${conversationId}/invite`, { requestingUser, targetUserId });
    return response.data;
};

export const updateEmail = async (username: string, currentPassword: string, newEmail: string) => {
    const response = await api.post('auth/update-email', { username, currentPassword, newEmail });
    return response.data;
};

export const revertEmail = async (username: string, token: string) => {
    const response = await api.post('auth/revert-email', { username, token });
    return response.data;
};

export const createVendorOnServer = async (parentUsername: string, name: string, bio?: string) => {
    const response = await api.post('vendors', { parentUsername, name, bio });
    return response.data;
};

export const fetchIdeas = async (username: string) => {
    const response = await api.get(`users/${username}/ideas`);
    return response.data;
};

export const deleteIdea = async (username: string, ideaId: string) => {
    const response = await api.delete(`users/${username}/ideas/${ideaId}`);
    return response.data;
};

// --- BZZT BZZT MOD ---
export const fetchBzztTasks = async (username: string) => {
    const response = await api.get(`users/${username}/bzzt-tasks`);
    return response.data;
};

export const addBzztTask = async (username: string, task: any) => {
    const response = await api.post(`users/${username}/bzzt-tasks`, { task });
    return response.data;
};

export const deleteBzztTask = async (username: string, taskId: string) => {
    const response = await api.delete(`users/${username}/bzzt-tasks/${taskId}`);
    return response.data;
};

export const completeBzztTask = async (username: string, taskId: string) => {
    const response = await api.post(`users/${username}/bzzt-tasks/${taskId}/complete`);
    return response.data;
};

export const procrastinateBzztTask = async (username: string, taskId: string, excuse: string) => {
    const response = await api.post(`users/${username}/bzzt-tasks/${taskId}/procrastinate`, { excuse });
    return response.data;
};

export const fetchBzztMotivationalMessages = async (username: string) => {
    const response = await api.get(`users/${username}/bzzt-messages`);
    return response.data;
};

export const addBzztMotivationalMessage = async (username: string, message: string) => {
    const response = await api.post(`users/${username}/bzzt-messages`, { message });
    return response.data;
};

export const deleteBzztMotivationalMessage = async (username: string, index: number) => {
    const response = await api.delete(`users/${username}/bzzt-messages/${index}`);
    return response.data;
};

export const updateBzztTask = async (username: string, taskId: string, task: any) => {
    const response = await api.put(`users/${username}/bzzt-tasks/${taskId}`, { task });
    return response.data;
};

export const fetchBzztProcrastinationRemarks = async (username: string) => {
    const response = await api.get(`users/${username}/bzzt-procrastination-remarks`);
    return response.data;
};

export const addBzztProcrastinationRemark = async (username: string, remark: string) => {
    const response = await api.post(`users/${username}/bzzt-procrastination-remarks`, { remark });
    return response.data;
};

export const deleteBzztProcrastinationRemark = async (username: string, index: number) => {
    const response = await api.delete(`users/${username}/bzzt-procrastination-remarks/${index}`);
    return response.data;
};

export default api;
export interface CustomReaction {
    id: string;
    name: string;
    url: string;
    mediaType: string;
    tags: string[];
    creatorId: string;
}

export const createCustomReaction = async (data: Partial<CustomReaction>): Promise<CustomReaction> => {
    const response = await api.post('/reactions', data);
    return response.data;
};

export const searchCustomReactions = async (query: string): Promise<CustomReaction[]> => {
    const response = await api.get('/reactions/search', { params: { q: query } });
    return response.data;
};
